import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useMe } from './useMe';
import { isInTeams, authenticateViaPopup } from './teamsContext';
import { SignInScene } from './SignInScene';

interface Props {
  children: ReactNode;
  adminOnly?: boolean;
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function FileTextIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" x2="8" y1="13" y2="13" />
      <line x1="16" x2="8" y1="17" y2="17" />
      <line x1="10" x2="8" y1="9" y2="9" />
    </svg>
  );
}

function RouteIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-page-tint animate-fade-in">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 rounded-full border-[2.5px] border-divider border-t-petrol animate-spin" />
        <p
          className="text-sm font-medium text-muted-slate animate-fade-in-up"
          style={{ animationDelay: '200ms' }}
        >
          Verifying identity...
        </p>
      </div>
    </div>
  );
}

function SignInPage() {
  const features = [
    { icon: <RouteIcon />, title: 'Automated routing', desc: 'Precedence rules + AI classify every inbound document' },
    { icon: <FileTextIcon />, title: 'Lifecycle management', desc: 'Triage, merge, and organise across SharePoint' },
    { icon: <ShieldIcon />, title: 'Full audit trail', desc: 'Every action logged for compliance and transparency' },
  ];

  return (
    <div className="flex min-h-dvh">
      {/* ── Left brand panel (hidden on mobile) ── */}
      <div
        className="relative hidden w-[46%] overflow-hidden lg:flex lg:flex-col lg:justify-between animate-slide-in-left animate-aurora"
        style={{ background: 'linear-gradient(160deg, #002a3a 0%, #003d52 20%, #005677 40%, #007a9a 60%, #005677 80%, #003d52 100%)' }}
      >
        <SignInScene />
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            background: 'radial-gradient(ellipse 80% 70% at 30% 40%, transparent 0%, rgba(0, 42, 58, 0.45) 100%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          aria-hidden="true"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0z' fill='none'/%3E%3Cpath d='M0 20h40M20 0v40' stroke='%23fff' stroke-width='0.5'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Panel content */}
        <div className="relative z-10 flex flex-1 flex-col justify-center px-12 py-16 xl:px-16">
          <div
            className="mb-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm animate-fade-in-up"
            style={{ animationDelay: '100ms' }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14 2 14 8 20 8" />
              <path d="m9 15 2 2 4-4" />
            </svg>
          </div>

          <h1
            className="text-[2rem] font-bold leading-tight tracking-tight text-white xl:text-[2.5rem] animate-fade-in-up"
            style={{ animationDelay: '200ms' }}
          >
            Document routing,<br />
            <span
              className="inline-block animate-fade-in-up"
              style={{ color: '#3ac1cd', animationDelay: '300ms' }}
            >
              automated.
            </span>
          </h1>
          <p
            className="mt-5 max-w-sm text-[0.9375rem] leading-relaxed text-white/65 animate-fade-in-up"
            style={{ animationDelay: '400ms' }}
          >
            Intelligent classification and lifecycle management for every document that enters your organisation.
          </p>

          {/* Stats row */}
          <div
            className="mt-14 flex gap-8 animate-fade-in-up"
            style={{ animationDelay: '500ms' }}
          >
            {[
              
              { value: 'AI', label: 'Powered routing', accent: true },
              { value: '100%', label: 'Audit coverage' },
            ].map((stat) => (
              <div key={stat.label} className="min-w-0">
                <div
                  className="text-2xl font-bold"
                  style={{ color: stat.accent ? '#3ac1cd' : '#fff' }}
                >
                  {stat.value}
                </div>
                <div className="mt-1.5 text-[0.6875rem] font-medium uppercase tracking-wider text-white/45">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="relative z-10 border-t border-white/10 px-12 py-5 xl:px-16 animate-fade-in"
          style={{ animationDelay: '600ms' }}
        >
          <p className="text-xs font-medium text-white/40">
            DIH Hub &middot; Document Organisation
          </p>
        </div>
      </div>

      {/* ── Right sign-in panel ── */}
      <div className="flex flex-1 flex-col bg-page-tint">
        {/* Mobile-only top brand accent */}
        <div className="h-1 w-full bg-petrol lg:hidden" />

        <div className="flex flex-1 items-center justify-center px-5 py-12">
          <div className="w-full max-w-[400px]">
            {/* Mobile-only logo header */}
            <div className="mb-8 text-center lg:hidden animate-scale-in">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-petrol">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="m9 15 2 2 4-4" />
                </svg>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-navy">
                DIH Hub
              </h1>
              <p className="mt-1 text-sm text-muted-slate">
                Intelligent document routing & lifecycle management
              </p>
            </div>

            {/* Sign-in card */}
            <div
              className="overflow-hidden rounded-xl border border-divider bg-white animate-scale-in"
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}
            >
              {/* Brand accent bar */}
              <div className="h-[3px] bg-petrol" />

              <div className="p-7 sm:p-9">
                {/* Desktop greeting */}
                <div className="mb-1 hidden lg:block">
                  <div className="mb-6 animate-fade-in-up">
                    <img src="/images/di-logo.svg" alt="Digital Infrastructure" className="h-10" />
                  </div>
                </div>

                <h2
                  className="text-lg font-bold tracking-tight text-navy animate-fade-in-up"
                  style={{ animationDelay: '200ms' }}
                >
                  Welcome
                </h2>
                <p
                  className="mt-1.5 text-sm leading-relaxed text-muted-slate animate-fade-in-up"
                  style={{ animationDelay: '300ms' }}
                >
                  Sign in with your corporate account to access the document management platform.
                </p>

                {/* Microsoft SSO button */}
                <a
                  href="/.auth/login/aad"
                  className="group mt-7 flex w-full cursor-pointer items-center justify-center gap-3 rounded-lg bg-petrol px-4 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-darkest-teal active:bg-darkest-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal animate-fade-in-up animate-cta-glow"
                  style={{ animationDelay: '400ms' }}
                >
                  <MicrosoftLogo />
                  <span>Sign in with Microsoft</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto opacity-50 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </a>

                {/* Divider */}
                <div className="my-7 border-t border-divider" />

                {/* Feature cards — staggered entrance */}
                <div className="space-y-2.5">
                  {features.map((f, i) => (
                    <div
                      key={f.title}
                      className="flex items-start gap-3 rounded-lg bg-page-tint p-3.5 transition-colors duration-200 hover:bg-card-fill animate-fade-in-up"
                      style={{ animationDelay: `${500 + i * 100}ms` }}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-petrol/[0.08] text-petrol">
                        {f.icon}
                      </span>
                      <div>
                        <div className="text-[0.8125rem] font-semibold text-navy">{f.title}</div>
                        <div className="mt-0.5 text-xs leading-relaxed text-muted-slate">{f.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              className="mt-6 flex items-center justify-center gap-1.5 text-xs text-faint-slate animate-fade-in"
              style={{ animationDelay: '800ms' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Protected by Microsoft Entra ID&ensp;&middot;&ensp;DIH internal use only</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminRequired() {
  return (
    <div className="flex min-h-dvh flex-col bg-page-tint">
      <div className="h-1 w-full bg-petrol" />
      <div className="flex flex-1 items-center justify-center px-5 py-12">
        <div className="w-full max-w-[420px]">
          <div className="overflow-hidden rounded-lg border border-divider bg-white shadow-sm">
            <div className="h-[3px] bg-accent-3" />
            <div className="p-6 sm:p-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-3/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-navy">Admin access required</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-slate">
                Your account is signed in but does not have admin rights for this action.
                Contact an IT administrator to request elevated access.
              </p>
              <a
                href="/"
                className="brand-btn brand-btn-secondary mt-5 inline-flex"
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamsAuthFallback({ onSuccess }: { onSuccess: () => void }) {
  const [state, setState] = useState<'trying' | 'failed'>('trying');

  useEffect(() => {
    let cancelled = false;
    authenticateViaPopup().then((ok) => {
      if (cancelled) return;
      if (ok) {
        onSuccess();
      } else {
        setState('failed');
      }
    });
    return () => { cancelled = true; };
  }, [onSuccess]);

  const handleRetry = useCallback(() => {
    setState('trying');
    authenticateViaPopup().then((ok) => {
      if (ok) onSuccess();
      else setState('failed');
    });
  }, [onSuccess]);

  if (state === 'trying') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-page-tint animate-fade-in">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 rounded-full border-[2.5px] border-divider border-t-petrol animate-spin" />
          <p className="text-sm font-medium text-muted-slate">
            Signing in via Microsoft...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-page-tint animate-fade-in">
      <div className="w-full max-w-[400px] px-5">
        <div
          className="overflow-hidden rounded-xl border border-divider bg-white"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)' }}
        >
          <div className="h-[3px] bg-accent-3" />
          <div className="p-7 text-center sm:p-9">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-3/10">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-base font-semibold text-navy">Teams sign-in failed</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-slate">
              Unable to authenticate via Microsoft Teams SSO. Please ensure your
              Teams account has access to this application, or contact your IT
              administrator.
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="brand-btn brand-btn-secondary mt-5 inline-flex"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RequireAuth({ children, adminOnly = false }: Props) {
  const isPreview = new URLSearchParams(window.location.search).has('preview');
  const me = useMe();
  const [inTeams, setInTeams] = useState<boolean | null>(null);

  const handleTeamsAuthSuccess = useCallback(() => {
    me.refetch();
  }, [me]);

  useEffect(() => {
    isInTeams().then(setInTeams);
  }, []);

  if (isPreview) return <>{children}</>;

  if (me.isPending || (me.isError && inTeams === null)) return <LoadingSpinner />;

  if (me.isError || !me.data?.isAuthenticated) {
    return inTeams ? <TeamsAuthFallback onSuccess={handleTeamsAuthSuccess} /> : <SignInPage />;
  }

  if (adminOnly && !me.data.isAdmin) return <AdminRequired />;

  return <>{children}</>;
}
